import { useQuery } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  ArrowRight,
  BarChart3,
  Bug,
  Calendar,
  CheckSquare,
  CircleDot,
  Crown,
  FileText,
  Flame,
  FolderKanban,
  Kanban,
  Layers,
  ListTodo,
  Minus,
  Sparkles,
  User,
  UsersRound,
} from 'lucide-react';
import { Link, useOutletContext } from 'react-router-dom';

import { EmptyState } from '@/components/common/empty-state';
import { useIssueDrawerStore } from '@/stores/issue-drawer-store';
import type { ProjectLayoutContext } from '@/components/layout/project-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { extractApiErrorMessage } from '@/lib/api';
import {
  getProjectByKey,
  listIssues,
  type IssuePriority,
  type IssueSummary,
  type IssueType,
  type StatusCategory,
} from '@/lib/project-api';
import { cn, initialsFrom } from '@/lib/utils';

const TYPE_META: Record<
  IssueType,
  { label: string; icon: typeof FileText; iconColor: string; bg: string }
> = {
  STORY: {
    label: 'Story',
    icon: FileText,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-500/10 ring-emerald-500/20',
  },
  TASK: {
    label: 'Task',
    icon: CheckSquare,
    iconColor: 'text-sky-600 dark:text-sky-400',
    bg: 'bg-sky-500/10 ring-sky-500/20',
  },
  BUG: {
    label: 'Bug',
    icon: Bug,
    iconColor: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-500/10 ring-rose-500/20',
  },
  SUBTASK: {
    label: 'Subtask',
    icon: CircleDot,
    iconColor: 'text-slate-500 dark:text-slate-400',
    bg: 'bg-muted ring-border',
  },
};

const PRIORITY_META: Record<
  IssuePriority,
  { label: string; icon: typeof ArrowUp; color: string }
> = {
  LOWEST: { label: 'Lowest', icon: ArrowDown, color: 'text-slate-400' },
  LOW: { label: 'Low', icon: ArrowDown, color: 'text-emerald-500' },
  MEDIUM: { label: 'Medium', icon: Minus, color: 'text-amber-500' },
  HIGH: { label: 'High', icon: ArrowUp, color: 'text-orange-500' },
  HIGHEST: { label: 'Highest', icon: Flame, color: 'text-rose-500' },
};

const STATUS_CATEGORY_CLASS: Record<StatusCategory, string> = {
  TODO: 'bg-slate-500/10 text-slate-700 ring-slate-500/30 dark:text-slate-300',
  IN_PROGRESS: 'bg-sky-500/10 text-sky-700 ring-sky-500/30 dark:text-sky-300',
  DONE: 'bg-emerald-500/10 text-emerald-700 ring-emerald-500/30 dark:text-emerald-300',
};

export function ProjectOverviewPage() {
  const { orgId, orgSlug, projectKey } = useOutletContext<ProjectLayoutContext>();

  const projectQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey],
    queryFn: () => getProjectByKey(orgId, projectKey),
  });

  const recentIssuesQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey, 'recent-issues'],
    queryFn: async () => {
      const project = projectQuery.data;
      if (!project) return null;
      return listIssues(orgId, project.id, { limit: 6, sort: 'updatedAt', order: 'desc' });
    },
    enabled: Boolean(projectQuery.data),
  });

  const project = projectQuery.data;
  const leadName = project?.leadUser?.name ?? null;
  const leadInitial = leadName?.trim().charAt(0).toUpperCase() ?? '?';
  const createdLabel = project?.createdAt
    ? new Date(project.createdAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 p-6">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
        <div
          className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-primary/10 blur-3xl"
          aria-hidden
        />
        <div className="relative flex flex-wrap items-start justify-between gap-4 p-6">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <Layers className="h-6 w-6" aria-hidden />
            </div>
            <div className="min-w-0">
              {projectQuery.isLoading ? (
                <>
                  <Skeleton className="h-7 w-56" />
                  <Skeleton className="mt-2 h-4 w-64" />
                </>
              ) : project ? (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="truncate text-2xl font-semibold tracking-tight">
                      {project.name}
                    </h1>
                    <span className="inline-flex items-center gap-1 rounded-md bg-background/80 px-2 py-0.5 font-mono text-xs font-medium ring-1 ring-border">
                      {project.key}
                    </span>
                  </div>
                  {project.description ? (
                    <p className="mt-1 line-clamp-2 max-w-2xl text-sm text-muted-foreground">
                      {project.description}
                    </p>
                  ) : (
                    <p className="mt-1 text-sm italic text-muted-foreground">
                      No description yet.
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    {leadName ? (
                      <span className="inline-flex items-center gap-1.5 rounded-md bg-background/80 px-2 py-0.5 ring-1 ring-border">
                        <Crown className="h-3 w-3 text-amber-500" aria-hidden />
                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/10 text-[9px] font-semibold uppercase text-primary ring-1 ring-primary/20">
                          {leadInitial}
                        </span>
                        <span className="text-muted-foreground">{leadName}</span>
                      </span>
                    ) : null}
                    {createdLabel ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-background/80 px-2 py-0.5 text-muted-foreground ring-1 ring-border">
                        <Calendar className="h-3 w-3" aria-hidden />
                        Created {createdLabel}
                      </span>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
        {project ? (
          <div className="grid grid-cols-2 gap-px border-t bg-border sm:grid-cols-3">
            <OverviewStat
              icon={ListTodo}
              label="Open issues"
              value={String(project.openIssueCount)}
              tone="primary"
            />
            <OverviewStat
              icon={UsersRound}
              label="Members"
              value={String(project.memberCount)}
              tone="primary"
            />
            <OverviewStat
              icon={FolderKanban}
              label="Statuses"
              value={String(project.statusCount)}
              tone="primary"
            />
          </div>
        ) : null}
      </div>

      {/* Quick-link cards */}
      <div className="grid gap-3 md:grid-cols-3">
        <QuickLink
          to="board"
          icon={Kanban}
          title="Board"
          description="Drag cards across the active sprint."
          iconBg="bg-sky-500/10 text-sky-600 ring-sky-500/20 dark:text-sky-400"
        />
        <QuickLink
          to="backlog"
          icon={Layers}
          title="Backlog"
          description="Plan sprints and triage issues."
          iconBg="bg-emerald-500/10 text-emerald-600 ring-emerald-500/20 dark:text-emerald-400"
        />
        <QuickLink
          to="dashboard"
          icon={BarChart3}
          title="Dashboard"
          description="Burndown, distributions, trends."
          iconBg="bg-violet-500/10 text-violet-600 ring-violet-500/20 dark:text-violet-400"
        />
      </div>

      {/* Recently updated */}
      <Card className="overflow-hidden">
        <div className="h-1 w-full bg-gradient-to-r from-primary/60 via-primary/30 to-transparent" />
        <div className="flex flex-row items-center justify-between gap-3 border-b bg-muted/30 px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/20">
              <Sparkles className="h-4 w-4" aria-hidden />
            </div>
            <h3 className="text-base font-semibold">Recently updated issues</h3>
          </div>
          <Link
            to="issues"
            className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:underline"
          >
            View all <ArrowRight className="h-3 w-3" aria-hidden />
          </Link>
        </div>
        <CardContent className="p-3">
          {recentIssuesQuery.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : recentIssuesQuery.isError ? (
            <EmptyState
              title="Could not load issues"
              description={extractApiErrorMessage(recentIssuesQuery.error)}
            />
          ) : !recentIssuesQuery.data || recentIssuesQuery.data.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/20 py-10 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-background ring-1 ring-border">
                <ListTodo className="h-4 w-4 text-muted-foreground" aria-hidden />
              </div>
              <p className="text-sm font-medium">No issues yet</p>
              <p className="text-xs text-muted-foreground">
                Create the first issue from the Issues tab.
              </p>
            </div>
          ) : (
            <ul className="space-y-1.5">
              {recentIssuesQuery.data.items.map((issue) => (
                <RecentIssueRow
                  key={issue.id}
                  issue={issue}
                  orgId={orgId}
                  orgSlug={orgSlug}
                  projectKey={projectKey}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function OverviewStat({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Layers;
  label: string;
  value: string;
  tone?: 'primary';
}) {
  return (
    <div className="flex items-center gap-3 bg-background px-4 py-3">
      <div
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-xl ring-1',
          tone === 'primary'
            ? 'bg-primary/5 text-primary ring-primary/20'
            : 'bg-muted text-muted-foreground ring-border',
        )}
      >
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="truncate text-xl font-semibold tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function QuickLink({
  to,
  icon: Icon,
  title,
  description,
  iconBg,
}: {
  to: string;
  icon: typeof Kanban;
  title: string;
  description: string;
  iconBg: string;
}) {
  return (
    <Link
      to={to}
      className="group relative flex items-start gap-3 overflow-hidden rounded-xl border bg-card p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
    >
      <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1', iconBg)}>
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 text-sm font-semibold">
          {title}
          <ArrowRight className="h-3.5 w-3.5 translate-x-0 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100" aria-hidden />
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
    </Link>
  );
}

function RecentIssueRow({
  issue,
  orgId,
  orgSlug,
  projectKey,
}: {
  issue: IssueSummary;
  orgId: string;
  orgSlug: string;
  projectKey: string;
}) {
  const openIssue = useIssueDrawerStore((s) => s.open);
  const typeMeta = TYPE_META[issue.type];
  const TypeIconCmp = typeMeta.icon;
  const priMeta = PRIORITY_META[issue.priority];
  const PriIconCmp = priMeta.icon;
  const initials = issue.assigneeName
    ? initialsFrom(issue.assigneeName, issue.assigneeEmail ?? undefined)
    : '';
  const updated = new Date(issue.updatedAt);
  const formatted = updated.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const open = () => openIssue({ orgId, orgSlug, projectKey, issueKey: issue.key });

  return (
    <li
      onClick={open}
      className="group flex cursor-pointer items-center gap-3 rounded-lg border bg-background px-2.5 py-2 text-sm shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
    >
      <div
        className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-md ring-1', typeMeta.bg)}
        title={typeMeta.label}
      >
        <TypeIconCmp className={cn('h-3.5 w-3.5', typeMeta.iconColor)} aria-hidden />
      </div>
      <PriIconCmp
        className={cn('h-4 w-4 shrink-0', priMeta.color)}
        aria-label={`Priority ${priMeta.label}`}
      />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          open();
        }}
        className="shrink-0 font-mono text-[11px] font-medium text-primary hover:underline"
      >
        {issue.key}
      </button>
      <span className="min-w-0 flex-1 truncate text-sm group-hover:text-primary">
        {issue.title}
      </span>
      <span
        className={cn(
          'hidden rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 sm:inline-flex',
          STATUS_CATEGORY_CLASS[issue.statusCategory],
        )}
      >
        {issue.statusName}
      </span>
      {issue.assigneeName ? (
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold uppercase text-primary ring-1 ring-primary/20"
          title={issue.assigneeName}
        >
          {initials}
        </span>
      ) : (
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground ring-1 ring-border"
          title="Unassigned"
        >
          <User className="h-3 w-3" aria-hidden />
        </span>
      )}
      <span className="hidden shrink-0 items-center gap-1 text-[11px] text-muted-foreground md:inline-flex">
        <Calendar className="h-3 w-3" aria-hidden />
        {formatted}
      </span>
    </li>
  );
}
